<template>
  <div>
    <div v-for="(alert, index) in popups" :key="index">
      <v-dialog v-model="alert.isVisible" persistent :max-width="550">
        <div class="dialog">
          <div class="dialog-header" :class="`_${getType(alert.type)}`">
            <div class="dialog-header__logo">
              <img alt="XTUR Sp. z o.o." src="@/assets/images/layout/logo-page-white.svg" />
            </div>
            <div class="dialog-header__text">
              <div>
                <v-icon size="37">{{ `$dialog${capitalize(getType(alert.type))}` }}</v-icon>
                <span>
                  {{ alert.title }}
                </span>
              </div>
            </div>
          </div>
          <div class="dialog-body">
            <div>
              <div v-for="(text, num) in alert.content.split('\n')" :key="num" style="min-height: 19.5px">
                <span v-html="text" />
              </div>
            </div>
            <div v-if="alert.referenceNumber" class="dialog-body__textarea">
              <span
                >Możesz też opisać sytuacje, w której powstał błąd. Przydatne będą szczegóły takie jak numer
                rezerwacji/dokumentu, kontrahent. Kliknij wyślij, lub pomiń ten krok i kliknij zamknij.
              </span>
              <v-form :ref="alert.referenceNumber">
                <v-textarea
                  v-model="alert.description"
                  :validate-on-blur="false"
                  rounded
                  hide-details="auto"
                  dense
                  height="100px"
                  style="height: 100px"
                  placeholder="Opis"
                  counter="1000"
                  :rules="[maxLengthValidator(alert.description, 1000)]"
                >
                </v-textarea>
              </v-form>
              <div style="margin-top: 20px">Numer referencyjny: {{ alert.referenceNumber }}</div>
            </div>
          </div>
          <div class="dialog-actions end">
            <v-tooltip v-if="alert.referenceNumber" bottom>
              <template #activator="{ on }">
                <div v-on="on">
                  <v-btn
                    class="dialog-btn"
                    :disabled="isLoading || alert.description == null || alert.description == ''"
                    elevation="0"
                    small
                    outlined
                    :color="getType(alert.type)"
                    @click="closeAndSend(alert)"
                  >
                    {{ $t("send") | capitalize }}
                  </v-btn>
                </div>
              </template>
              Wpisz wiadomość, aby wysłać
            </v-tooltip>
            <v-btn
              class="dialog-btn"
              :disabled="isLoading"
              elevation="0"
              small
              :color="getType(alert.type)"
              @click="close(alert)"
            >
              {{ $t("close") | capitalize }}
            </v-btn>
          </div>
        </div>
      </v-dialog>
    </div>
  </div>
</template>

<script lang="ts">
import "reflect-metadata";
import { Component, Vue, Watch } from "vue-property-decorator";
import { AlertModel } from "@/models/components/popup/Alert";
import { Resolver } from "@/models/components/popup/Resolver";
import IsVisible from "@/models/components/popup/IsVisible";
import { MESSAGE_TYPES } from "@/static/messageTypes";
import { filterCapitalize } from "@/filters/capitalize";
import html2canvas from "html2canvas";
import { Details } from "@/models/components/popup/Details";
import { postSaveInternalErrorInformation } from "@/requests/error";
import maxLengthValidator from "@/rules/maxLengthValidator";
import { Validatable } from "@/models/components/inputs/validatable";

@Component
export default class AlertPopup extends Vue {
  get isLoading(): boolean {
    return this.$store.direct.getters.isLoading;
  }
  get popups(): Array<AlertModel & Resolver & IsVisible> {
    return this.$store.direct.state.popup.alerts;
  }

  capitalize = filterCapitalize;
  maxLengthValidator = maxLengthValidator;

  getType(type: MESSAGE_TYPES): string {
    switch (type) {
      case MESSAGE_TYPES.Success:
        return "success";
      case MESSAGE_TYPES.Warning:
        return "warning";
      case MESSAGE_TYPES.Error:
        return "error";
      default:
        return "";
    }
  }

  close(alert: AlertModel & Resolver & IsVisible): void {
    alert.resolver(true);
    alert.isVisible = false;
  }

  async closeAndSend(alert: AlertModel & Resolver & IsVisible & Details): Promise<void> {
    if (alert.referenceNumber) {
      const ref = this.$refs[alert.referenceNumber];

      if (Array.isArray(ref)) {
        const field = ref[0] as Vue & Validatable;
        if (!field.validate()) {
          this.$store.direct.dispatch.popup.addFormErrorsSnackbar();
          return;
        }
      } else {
        const field = ref as Vue & Validatable;
        if (!field.validate()) {
          this.$store.direct.dispatch.popup.addFormErrorsSnackbar();
          return;
        }
      }
    }

    alert.resolver(true);
    alert.isVisible = false;

    await postSaveInternalErrorInformation(alert.referenceNumber, alert.description, alert.screen).catch(() => {
      return null;
    });
  }

  @Watch("popups")
  async screen(popups: Array<AlertModel & Resolver & IsVisible & Details>) {
    // Canvas rendering is too slow on Firefox #2028
    // https://github.com/niklasvh/html2canvas/issues/2028

    // eslint-disable-next-line
    CanvasRenderingContext2D.prototype.clip = function() {};

    const application = document.getElementById("application");

    // Only error types
    const items = popups.filter((x) => x.isVisible && x.screen == null && x.type == MESSAGE_TYPES.Error);

    if (application && items.length > 0) {
      const data = await html2canvas(application);

      const base64ImageUrl = data.toDataURL("image/png", 1);

      for (const popup of items) {
        popup.screen = base64ImageUrl;
      }
    }
  }
}
</script>

<style lang="scss">
.v-dialog {
  .v-card.v-card--flat {
    padding: 0;
  }
}

.dialog {
  width: 100%;
  height: auto;
  min-height: 290px;
  background-color: white;
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.dialog-header {
  position: relative;
  height: 110px;
}

.dialog-header::before {
  content: "";
  position: absolute;
  height: 110px;
  width: calc(100% + 300px);
  top: 0;
  left: -150px;
  border-radius: 0 0 50% 50% / 0 0 3em 3em;
  display: block;
  background-color: white;
}

.dialog-header._error::before {
  background-color: $error;
}
.dialog-header._warning::before {
  background-color: $warning;
}
.dialog-header._success::before {
  background-color: $success;
}

.dialog-header__text {
  color: white;
  display: block;
  position: absolute;
  left: 50%;
  top: 50%;
  -webkit-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
  font-size: 20px;
  font-weight: 600;
}

.dialog-header__text span {
  vertical-align: middle;
}

.dialog-header__logo {
  position: absolute;
  top: 43px;
  left: 23px;
}

.dialog-body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  padding: 20px 20px 10px 20px;
  span {
    color: black;
    font-size: 13px;
    word-wrap: break-word;
  }
}

.dialog-body__textarea {
  margin-top: 15px;
}

.dialog-actions {
  display: flex;
  flex-direction: row;
  gap: 10px;
  padding: 10px 20px 20px 20px;
  height: auto;
  &.end {
    justify-content: flex-end;
  }
}

.dialog-btn.v-btn--outlined {
  border: thin solid $input-border !important;
}

.dialog-body {
  .v-input.v-textarea {
    .v-input__slot {
      padding-right: 0 !important;
      textarea {
        height: 100px;
        resize: none !important;
      }
    }
  }
}
</style>
